import { ApiTablePage } from 'components/ApiTablePage/ApiTablePage';
import {
  TabbedTables,
  type TableTabRoute,
} from 'components/TabbedTables/TabbedTables';
import type { TableRowMeta } from 'components/Table/model/TableRowMeta';
import { produce } from 'immer';
import { useMemo } from 'react';
import type { FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createTableApiScopeRequirement } from 'utils/rbac/helpers/createTableApiScopeRequirement';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';

type Props = {
  className?: string;
};

const patchRow = (
  row: TableRowMeta & { status: 'added' | 'removed' | 'not changed' },
) =>
  produce(row, (draft) => {
    if (['added', 'removed'].includes(row.status)) {
      draft.$severity = draft.$severity ?? {};
      draft.$severity.status = row.status === 'added' ? 0 : 30;
    }
  });
const makeChangesPage =
  (apiEndpoint: string): FC =>
  () => {
    const [searchParams] = useSearchParams();
    const before = searchParams.get('before');
    const after = searchParams.get('after');

    const bindVariables = useMemo(() => ({ before, after }), [before, after]);

    const endpoint = useTableEndpoint({ apiEndpoint });

    return (
      <ApiTablePage
        endpoint={endpoint}
        additionalApiTableProps={{
          bindVariables,
          className: 'flex-1',
          disableViewUrl: true,
          patchRow,
        }}
      />
    );
  };

const DevicesPage = makeChangesPage('/tables/management/changes/devices');
const PartNumbersPage = makeChangesPage('/tables/management/changes/pn');
const ManagedIpPage = makeChangesPage(
  '/tables/management/changes/managed-devs',
);
const ConnectivityMatrixPage = makeChangesPage(
  '/tables/management/changes/connectivity',
);

export const changesSummaryRoutes: TableTabRoute[] = [
  {
    path: '/management/changes/result/devices',
    element: <DevicesPage />,
    getTabLabel: (msg) => msg.managementChanges.summary.tabs.devices,
    requiredApiScope: createTableApiScopeRequirement(
      '/tables/management/changes/devices',
    ),
  },
  {
    path: '/management/changes/result/part-numbers',
    element: <PartNumbersPage />,
    getTabLabel: (msg) => msg.managementChanges.summary.tabs.partNumbers,
    requiredApiScope: createTableApiScopeRequirement(
      '/tables/management/changes/pn',
    ),
  },
  {
    path: '/management/changes/result/managed-ip',
    element: <ManagedIpPage />,
    getTabLabel: (msg) => msg.managementChanges.summary.tabs.managedIp,
    requiredApiScope: createTableApiScopeRequirement(
      '/tables/management/changes/managed-devs',
    ),
  },
  {
    path: '/management/changes/result/connectivity-matrix',
    element: <ConnectivityMatrixPage />,
    getTabLabel: (msg) => msg.managementChanges.summary.tabs.connectivityMatrix,
    requiredApiScope: createTableApiScopeRequirement(
      '/tables/management/changes/connectivity',
    ),
  },
];

export const ChangesSummary: FC<Props> = (props) => (
  <TabbedTables
    className={props.className}
    rootPath='/management/changes'
    preserveQueryString
    tabs={changesSummaryRoutes}
  />
);
